import React, { useState } from 'react';
import { analyzeCall, requestCallback, sendFeedback } from '../services/api';
import Loader from './Loader';
import SkeletonCard from './SkeletonCard';
import FeedbackCard from './FeedbackCard';
import CallbackModal from './CallbackModal';
import { FiUpload, FiPlay } from 'react-icons/fi';

export default function UploadForm() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [error, setError] = useState('');

  const handleDrop = e => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    try {
      const result = await analyzeCall(file);
      setFeedback({ ...result, submitted: false });
    } catch (err) {
      const msg =
       err.response?.data?.detail ||
       err.message ||
       'Something went wrong. Please try again.'
      setError(msg)
    } finally {
      setLoading(false);
    }
    
  };

  const handleFeedback = async helpful => {
    if (!feedback?.call_id || feedback.submitted) return;
    await sendFeedback({ call_id: feedback.call_id, helpful });
    setFeedback(f => ({ ...f, submitted: true, helpful }));
  };

  const handleCallback = async form => {
    await requestCallback(form);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Drag & drop uploader */}
        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition 
            ${file ? 'border-blue-400' : 'border-gray-300 dark:border-gray-600'} 
            hover:border-blue-500 dark:hover:border-blue-400 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm`}
        >
          <div className="flex flex-col items-center">
            <FiUpload className="text-3xl text-blue-500 dark:text-blue-400 mb-2" />
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              {file ? file.name : 'Drag & drop your .wav/.mp3 here, or click to select'}
            </p>
            <input
              type="file"
              accept="audio/*"
              onChange={e => setFile(e.target.files[0])}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
          {loading && (
            <div className="absolute inset-0 bg-white/60 dark:bg-gray-800/60 flex items-center justify-center rounded-2xl">
              <div className="w-3/4 h-1 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 dark:bg-blue-400 animate-grow"></div>
              </div>
            </div>
          )}
        </div>

        {/* Analyze button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700
                       disabled:opacity-50 text-white font-semibold rounded-lg transition
                       w-full sm:w-auto justify-center"
          >
            {loading ? (
              <>
                <FiPlay className="animate-spin mr-2" /> Analyzing…
              </>
            ) : (
              <>
                <FiPlay className="mr-2" /> Analyze with AI
              </>
            )}
          </button>
        </div>
      </form>

      {/* Loader / Skeleton / Feedback */}
      <div className="mt-8">
        {loading && <Loader />}
        {!loading && feedback && (
          feedback.raw 
            ? <FeedbackCard data={feedback} onGiveFeedback={handleFeedback} />
            : <SkeletonCard />
        )}
      </div>

      {/* Callback */}
      {feedback && !loading && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setCallbackOpen(true)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Request a callback
          </button>
        </div>
      )}

      {callbackOpen && (
        <CallbackModal onClose={() => setCallbackOpen(false)} onSubmit={handleCallback} />
      )}
    </>
  );
}
